import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { AdminDashboardQuery } from 'src/generated/graphql';
import { routes } from 'src/routes';

type DataPoint = NonNullable<
  AdminDashboardQuery['adminDashboard']
>['weeklyActiveProjects'][0];

type OrgTotal = NonNullable<
  AdminDashboardQuery['adminDashboard']
>['projectTotalsByOrg'][0];

interface WeeklyActivityChartProps {
  data: DataPoint[];
  totals: OrgTotal[];
  valueLabel: string;
}

// Generate consistent colors for organizations
const COLORS = [
  '#8884d8',
  '#82ca9d',
  '#ffc658',
  '#ff7300',
  '#00C49F',
  '#FFBB28',
  '#FF8042',
  '#0088FE',
  '#a4de6c',
  '#d0ed57',
];

export const WeeklyActivityChart = ({
  data,
  totals,
  valueLabel,
}: WeeklyActivityChartProps) => {
  // Transform data for stacked bar chart
  // Group by week, with each org as a separate key
  // Limit to top 10 orgs by total (using true totals from backend)
  const { chartData, organizations } = useMemo(() => {
    const weekMap = new Map<string, Record<string, number | string>>();

    data.forEach((point) => {
      if (!weekMap.has(point.weekStart)) {
        weekMap.set(point.weekStart, { weekStart: point.weekStart });
      }

      const weekData = weekMap.get(point.weekStart)!;
      weekData[point.organizationId] = point.count;
    });

    // Use backend totals (already sorted by total desc), take top 10
    const topOrgs = totals.slice(0, 10);

    // Sort by date
    const sortedWeeks = Array.from(weekMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, weekData]) => weekData);

    const orgs = topOrgs.map((org, index) => ({
      id: org.organizationId,
      slug: org.organizationSlug,
      name: org.organizationName,
      color: COLORS[index % COLORS.length],
      total: org.total,
    }));

    return { chartData: sortedWeeks, organizations: orgs };
  }, [data, totals]);

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
        No activity data available.
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 flex gap-4" style={{ height: 400 }}>
      <div className="flex-1 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="weekStart"
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip
              labelFormatter={(label) => `Week of ${label}`}
              formatter={(value, name) => {
                const org = organizations.find((o) => o.id === name);
                return [value ?? 0, org?.name || String(name)];
              }}
            />
            {organizations.map((org) => (
              <Bar
                key={org.id}
                dataKey={org.id}
                stackId="a"
                fill={org.color}
                name={org.id}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="w-48 flex-shrink-0 overflow-y-auto">
        <table className="w-full text-sm">
          <tbody>
            {organizations.map((org) => (
              <tr key={org.id}>
                <td className="py-1 pr-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ backgroundColor: org.color }}
                  />
                </td>
                <td className="py-1 pr-2 truncate max-w-[120px]" title={org.name}>
                  <Link
                    to={routes.org.getPath({ orgSlug: org.slug })}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {org.name}
                  </Link>
                </td>
                <td className="py-1 text-right text-gray-500 tabular-nums">
                  {org.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
